import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { ArticleDTO, KitHistoryEntry, ReceiptDTO } from "../../models/ArticleDTO";

export type { ReceiptDTO, ReceiptWaStatus } from "../../models/ArticleDTO";

const BASE = "/admin/campus/";

export type KitPaymentSection = "store" | "newSale" | "kitSales";
/** Section a list is read through: kits (Entregas), kitSales (Ventas, 38) or newSale (Ventas for who only has Nueva Venta, 66). */
export type KitListSection = "kits" | "kitSales" | "newSale";

/** One product of an "Entregar Factura" (2026-10-29): simple (type + note) or pieza por pieza (items + warehouseId). */
export interface DeliveryBatchEntry {
	kitId: number;
	type?: "partial" | "total";
	note?: string;
	/** Old-catalog sale: what had been handed out before (text, no stock). */
	previous?: string;
	items?: { componentId?: number | null; articleId: number; quantity: number; serials?: string[] }[];
	warehouseId?: number | null;
}

/** Kits / productos (Sep 2026): bitácora, entregas, anulación y reactivación. */
export class KitsService {

	private clean(filters: Record<string, any>): Record<string, any> {
		const params: Record<string, any> = {};
		Object.keys(filters || {}).forEach((key) => { const value = filters[key]; if (value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length < 1)) params[key] = value; });
		return params;
	}

	/** Lists. tab: paid_undelivered | partially_paid | undelivered | delivered | cancelled | pending_unpaid | sold | all. section: kits | kitSales. */
	list = async (campusId: number, section: KitListSection, tab: string, filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}`, { params: { ...this.clean(filters), tab, page, perPage } });
	}

	salesTotals = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>): Promise<AxiosResponse<{ kits: number; students: number; sold: number; paid: number; pending: number }>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_salesTotals`, { params: this.clean(filters) });
	}

	/** Reporte de Ventas · Inscritos Recientes. */
	recentEnrollees = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_recentEnrollees`, { params: { ...this.clean(filters), page, perPage } });
	}

	/** Paid / partially paid kits not delivered, grouped by product with stock and shortage. */
	withoutInventory = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>): Promise<AxiosResponse<any[]>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_withoutInventory`, { params: this.clean(filters) });
	}

	studentsWithoutProducts = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_studentsWithoutProducts`, { params: { ...this.clean(filters), page, perPage } });
	}

	soldByAdvisor = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>): Promise<AxiosResponse<any[]>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_soldByAdvisor`, { params: this.clean(filters) });
	}

	/** Courses, periods, schedules, mentors, advisors, articles, weekdays of the campus for the filter bars. */
	filterOptions = async (campusId: number, section: KitListSection): Promise<AxiosResponse<any>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_filters`);
	}

	/** Excel file (blob). kind: kits | students | advisors | inventory. */
	report = async (campusId: number, section: "kits" | "kitSales", kind: string, tab: string, filters: Record<string, any>): Promise<AxiosResponse<Blob>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_report`, { params: { ...this.clean(filters), kind, tab }, responseType: "blob" });
	}

	/** Free note on a kit (history only). */
	addNote = async (campusId: number, kitId: number, note: string): Promise<AxiosResponse<{ message: string; kit: ArticleDTO }>> => {
		return axiosInstance.post(`${BASE}${campusId}/kits/${kitId}/_note`, { note });
	}

	/**
	 * Pago digital reportado por Ventas: comprobante → ticket en Pagos por Procesar. Multipart (archivos).
	 * section = the screen it is reported from, so the permission asked is that screen's (CREATE bit):
	 * "newSale" (Nueva Venta), "kitSales" (Control de Ventas) or "store" (Catálogo, the default).
	 */
	reportPayment = async (campusId: number, kitIds: number[], data: { typeId: number; amount: number | null; reference: string; paidAt: string; note: string }, files: File[], section: KitPaymentSection = "store"): Promise<AxiosResponse<{ message: string; intentId: number }>> => {
		const body = new FormData();
		body.append("kitIds", JSON.stringify(kitIds));
		body.append("typeId", String(data.typeId));
		if (data.amount !== null && data.amount !== undefined) body.append("amount", String(data.amount));
		if (data.reference) body.append("reference", data.reference);
		if (data.paidAt) body.append("paidAt", data.paidAt);
		if (data.note) body.append("note", data.note);
		files.forEach((file) => body.append("files[]", file));
		return axiosInstance.post(`${BASE}${campusId}/${section}/_kitPayment`, body);
	};

	/** Sellers eligible for kits/products in the campus (kits team per country). Section "store". */
	kitAdvisorCandidates = async (campusId: number): Promise<AxiosResponse<{ id: string; name: string; email: string | null; roles: string[] }[]>> => {
		return axiosInstance.get(`${BASE}${campusId}/store/_kitAdvisorCandidates`);
	}

	/** Bitácora of one kit, oldest first. section: "kits" (Control de Entregas, default) or "kitSales" (Control de Ventas). */
	getHistory = async (campusId: number, kitId: number, section: KitListSection = "kits"): Promise<AxiosResponse<KitHistoryEntry[]>> => {
		return axiosInstance.get<KitHistoryEntry[]>(`${BASE}${campusId}/${section}/${kitId}/_history`);
	}

	/** Bitácora of every kit of a student in the campus, newest first. */
	getStudentHistory = async (campusId: number, studentId: number, section: KitListSection = "kits"): Promise<AxiosResponse<KitHistoryEntry[]>> => {
		return axiosInstance.get<KitHistoryEntry[]>(`${BASE}${campusId}/${section}/students/${studentId}/_history`);
	}

	/**
	 * Partial or total delivery of an old-catalogue kit (section "kits"). Multipart since Oct 2026: mandatory note (the
	 * detail that goes on the receipt), the student's hand signature (PNG data URL) and an optional photo of the delivery.
	 * The backend builds the Comprobante de Entrega and sends it by WhatsApp from the campus Caja number; that send never
	 * fails the delivery, its outcome comes back in receipt.waStatus.
	 */
	registerDelivery = async (campusId: number, kitId: number, type: "partial" | "total", note: string, signature: string, photo: File | null,
		legacy?: { items: { articleId: number; quantity: number }[]; previous: string; warehouseId?: number | null }): Promise<AxiosResponse<{ message: string; kit: ArticleDTO; receipt: ReceiptDTO }>> => {
		const body = new FormData();
		body.append("type", type);
		body.append("note", note);
		if (legacy) {
			body.append("items", JSON.stringify(legacy.items));
			if (legacy.previous.trim()) body.append("previous", legacy.previous.trim());
			if (legacy.warehouseId) body.append("warehouseId", String(legacy.warehouseId));
		}
		body.append("signature", signature);
		if (photo) body.append("photo", photo);
		return axiosInstance.post(`${BASE}${campusId}/kits/${kitId}/_delivery`, body);
	}

	/** Entrega por factura (2026-10-29): the kit plus the other paid, still-owed products of its invoice(s). */
	invoiceGroup = async (campusId: number, kitId: number): Promise<AxiosResponse<{ invoiceIds: number[]; kits: ArticleDTO[] }>> => {
		return axiosInstance.get(`${BASE}${campusId}/kits/${kitId}/_invoiceGroup`);
	}

	/**
	 * Several products delivered at once with ONE signature/photo: one Comprobante and one WhatsApp. Each entry is a simple
	 * delivery (type + note, old catalog) or pieces (items + warehouseId, new catalog). All or nothing.
	 */
	registerDeliveryBatch = async (campusId: number, kits: DeliveryBatchEntry[], signature: string, photo: File | null): Promise<AxiosResponse<{ message: string; kitIds: number[]; receipt: ReceiptDTO | null; receiptError?: string }>> => {
		const body = new FormData();
		body.append("kits", JSON.stringify(kits));
		body.append("signature", signature);
		if (photo) body.append("photo", photo);
		return axiosInstance.post(`${BASE}${campusId}/kits/_deliveryBatch`, body);
	}

	/** The receipt's files are behind the session: fetch as blob and show them in WAPP's viewer. kind: pdf | firma | foto. */
	getReceiptFile = async (campusId: number, receiptId: number, kind: "pdf" | "firma" | "foto"): Promise<Blob> => {
		const response = kind === "pdf"
			? await axiosInstance.get(`${BASE}${campusId}/kits/receipts/${receiptId}/_pdf`, { responseType: "blob" })
			: await axiosInstance.get(`${BASE}${campusId}/kits/receipts/${receiptId}/_file`, { responseType: "blob", params: { kind } });
		return response.data as Blob;
	}

	/** Retry the WhatsApp of a receipt. Already sent → 422 unless force. */
	resendReceipt = async (campusId: number, receiptId: number, force = false): Promise<AxiosResponse<{ message: string; receipt: ReceiptDTO }>> => {
		return axiosInstance.post(`${BASE}${campusId}/kits/receipts/${receiptId}/_send`, force ? { force: 1 } : {});
	}

	/** Fix the delivery state directly, e.g. "Desconocido" → real state (section "kits"). */
	setDeliveryStatus = async (campusId: number, kitId: number, deliveryStatusId: number, note: string): Promise<AxiosResponse<{ message: string; kit: ArticleDTO }>> => {
		return axiosInstance.put(`${BASE}${campusId}/kits/${kitId}/_deliveryStatus`, { deliveryStatusId, note });
	}

	/** Anular a $0-paid kit with a mandatory reason (section "kitSales"). */
	cancel = async (campusId: number, kitId: number, reason: string): Promise<AxiosResponse<{ message: string; kit: ArticleDTO }>> => {
		return axiosInstance.post(`${BASE}${campusId}/kitSales/${kitId}/_cancel`, { reason });
	}

	/** Bring an "Anulado" kit back to "Pendiente" with a mandatory reason (section "kitSales"). */
	reactivate = async (campusId: number, kitId: number, reason: string): Promise<AxiosResponse<{ message: string; kit: ArticleDTO }>> => {
		return axiosInstance.post(`${BASE}${campusId}/kitSales/${kitId}/_reactivate`, { reason });
	}
}
